import { SidePanelProps, useModal, useSnackbar } from "@homebound/beam";
import { useFormState } from "@homebound/form-state";
import { useHistory } from "react-router-dom";
import { SaveTakeoffLineItemInput } from "src/generated/graphqlTypes";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import {
  addPlaceholderItemConfig,
  saveAddEditItem,
} from "src/routes/libraries/plan-package/takeoffs/components/AddItems/utils";
import { SaveTliResult } from "src/routes/libraries/plan-package/takeoffs/TakeoffsManagerContext";
import { createPlanPackageTakeoffUrl } from "src/RouteUrls";
import { AcceptSlotForm } from "./AcceptSlotForm";
import {
  ReviewSlotRequests_IncomingSlotFragment,
  useReviewSlotRequests_AcceptSlotMutation,
  useReviewSlotRequests_RejectSlotMutation,
} from "./ReviewSlotRequests.generated";

export function useSlotReviewPanel(
  slot: ReviewSlotRequests_IncomingSlotFragment | null,
  onSettle: VoidFunction,
  planPackageId: string,
  planPackageVersionId: string,
): SidePanelProps | undefined {
  const { openModal } = useModal();
  const { triggerNotice } = useSnackbar();
  const history = useHistory();
  const [acceptSlot] = useReviewSlotRequests_AcceptSlotMutation();
  const [rejectSlot] = useReviewSlotRequests_RejectSlotMutation();

  const formState = useFormState({
    config: addPlaceholderItemConfig,
    init: {
      input: slot ?? undefined,
      map: (s) => {
        const p = s.designPackagePlaceholder;
        return {
          itemId: p.item.id,
          item: p.item,
          unitOfMeasure: p.unitOfMeasure,
          unitOfMeasureId: p.unitOfMeasure.id,
          taskId: p.task?.id,
          optionIds: p.options.map((o) => o.id),
          // Location is set by the backend via `fulfillsPlanPackageIncomingSlotId`
          location: undefined,
          materialVariant: p.materialVariant ?? null,
          materialVariantId: p.materialVariant?.id,
          quantity: p.quantity ?? 0,
        };
      },
    },
  });

  if (!slot) return undefined;

  const placeholder = slot.designPackagePlaceholder;
  const location = placeholder.location.room?.name ?? placeholder.location.name;

  return {
    title: placeholder.name,
    content: (
      <AcceptSlotForm
        slot={slot}
        planPackageId={planPackageId}
        planPackageVersionId={planPackageVersionId}
        formState={formState}
      />
    ),
    primaryAction: {
      label: "Accept",
      icon: "check",
      onClick: async () => {
        const saveTli = (input: SaveTakeoffLineItemInput) => {
          return acceptSlot({
            variables: {
              readyPlanId: planPackageId,
              input: [{ ...input, fulfillsPlanPackageIncomingSlotId: slot.id }],
            },
            update(cache) {
              cache.evict({ id: cache.identify({ __typename: "PlanPackageIncomingSlot", id: slot.id }) });
              cache.gc();
            },
          }) as unknown as SaveTliResult;
        };
        const result = await saveAddEditItem({ formState, saveTli, isPlaceholderItem: true });
        triggerNotice({ message: `You have successfully accepted slot "${placeholder.name}"` });

        // Navigate to the new draft version if it changed
        const newVersionId = result?.data?.saveTakeoffLineItems.version.id;
        if (newVersionId && newVersionId !== planPackageVersionId) {
          history.push(createPlanPackageTakeoffUrl(planPackageId, newVersionId));
        }
        onSettle();
      },
    },
    secondaryAction: {
      label: "Reject",
      icon: "x",
      onClick: () =>
        openModal({
          content: (
            <ConfirmationModal
              title="Reject slot?"
              confirmationMessage={
                <p>
                  Are you sure you want to reject the slot <strong>{placeholder.name}</strong> in{" "}
                  <strong>{location}</strong>? This will not import it into Takeoff. Please notify the interior designer
                  and include a reason.
                </p>
              }
              label="Reject slot"
              onConfirmAction={() => {
                void rejectSlot({
                  variables: { input: { ids: [slot.id] } },
                  update(cache) {
                    cache.evict({ id: cache.identify({ __typename: "PlanPackageIncomingSlot", id: slot.id }) });
                    cache.gc();
                  },
                });
                triggerNotice({ message: `You have rejected slot "${placeholder.name}"` });
                onSettle();
              }}
            />
          ),
        }),
    },
  };
}
